/*
  Clients — top-level directory. Row click → /clients/:email drills into
  the ClientDetail page. Filter chips (Active 30d / Lapsed 90d / Open
  questionnaire), search, plus per-row Quick SMS + Send follow-up + View.
*/
import { isoDate as melDate } from "@/lib/intl";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Users, Eye, Send, MessageSquare, Loader2, UserPlus, UserPlus2, RefreshCw, GitMerge, Search, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import QuickSmsDialog from "@/components/QuickSmsDialog";
import DuplicateClientsDialog from "@/components/DuplicateClientsDialog";
import { workspacePath, workspacePathFor } from "@/lib/clientRoutes";

const FILTER_STORAGE_KEY_V2 = "clients.filter.v2";

const FILTERS = [
  { key: "active_today", label: "Active today", params: { activity: "active_today" } },
  { key: "all", label: "All", params: {} },
  { key: "active_30d", label: "Active this month", params: { activity: "active_30d" } },
  { key: "lapsed_90d", label: "Lapsed 90+ days", params: { activity: "lapsed_90d" } },
  { key: "open_q", label: "Has open questionnaire", params: { has_open_questionnaire: true } },
];

export default function Clients() {
  const navigate = useNavigate();
  const { can } = useAuth();
  // All active staff can open, view, record SOAP and send SMS on any client.
  const editable = can("owner", "manager", "staff");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(() => {
    // Sticky filter: default to "active_today" on first visit, then
    // remember whatever the operator last picked. Uses a fresh v2 key
    // so people who had the old "all" default still land on the new
    // scheduled-today view when they upgrade.
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY_V2);
      if (stored) return stored;
    } catch { /* SSR / private mode */ }
    return "active_today";
  });
  const [smsFor, setSmsFor] = useState(null);   // row when quick-sms dialog open
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupCount, setDupCount] = useState(null);
  const [syncing, setSyncing] = useState(false);

  async function openClient(emailOrRow) {
    // Two-arg polymorphism kept minimal: the directory grid passes
    // the *whole row* so we can navigate straight to ``?as=<cid>``
    // without another API hop. Legacy callers still pass a bare
    // email string.
    let email;
    let clientId = null;
    if (emailOrRow && typeof emailOrRow === "object") {
      email = emailOrRow.client_email;
      clientId = emailOrRow.client_id || null;
    } else {
      email = emailOrRow;
    }
    if (!email && !clientId) return;
    if (clientId) {
      // The directory row already carries the canonical id (Phase 2
      // expansion). Skip the disambiguation dance entirely.
      navigate(workspacePathFor(email, clientId));
      return;
    }
    // Legacy path — resolve via by-email lookup. Retained so search
    // hits from other surfaces (SMS thread, Reviews, etc.) keep
    // working even before their row shape carries client_id.
    try {
      const r = await api.get("/clients/by-email", { params: { email } });
      const rows = r.data?.clients || [];
      if (rows.length === 1) {
        navigate(workspacePath(rows[0]));
        return;
      }
      if (rows.length > 1) {
        navigate(workspacePath(rows[0]));
        return;
      }
    } catch {
      /* fall through to legacy */
    }
    navigate(`/clients/${encodeURIComponent(email)}`);
  }

  async function syncAcuity() {
    setSyncing(true);
    try {
      const r = await api.post("/intake/clients/sync-acuity");
      const synth = r.data?.no_email_synthesised || 0;
      toast.success("Acuity clients synced", {
        description: `${r.data.emails || 0} unique clients from ${r.data.scanned || 0} appointments`
          + (synth ? ` · ${synth} email-less client(s) linked via synthetic key` : ""),
      });
      await load();
    } catch (e) {
      toast.error("Sync failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSyncing(false); }
  }

  async function load() {
    setLoading(true);
    try {
      const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
      const params = { ...active.params };
      if (q) params.q = q;
      const r = await api.get("/intake/clients", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load clients", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }

  // Initial load
  useEffect(() => { load(); loadDupCount(); /* eslint-disable-next-line */ }, []);
  // Persist the filter selection across visits.
  useEffect(() => {
    try { localStorage.setItem(FILTER_STORAGE_KEY_V2, filter); } catch { /* noop */ }
  }, [filter]);

  // Debounce search + refetch on filter change
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filter]);

  async function loadDupCount() {
    try {
      const r = await api.get("/clients/duplicates");
      setDupCount(r.data?.count ?? 0);
    } catch {
      setDupCount(null);
    }
  }

  const rowCountLabel = useMemo(() => {
    if (loading) return "Loading…";
    return `${rows.length} ${rows.length === 1 ? "client" : "clients"}`;
  }, [rows.length, loading]);

  function fmtDT(v) {
    if (!v) return "—";
    try { return new Date(v).toLocaleString(); } catch { return String(v); }
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Clients
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 flex items-center gap-3">
            <Users className="h-8 w-8" /> Clients
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Every client we&apos;ve treated. Click any row to open their profile — SOAP notes,
            intake forms, SMS thread, appointments and progress trends. Client not listed yet?
            Use <em>Open client by email</em> to jump straight to any Acuity client.
          </p>
        </div>
        {editable && (
          <div className="flex gap-2 flex-wrap">
            {can("owner", "manager") && (
              <Button
                variant="outline"
                onClick={() => setDupOpen(true)}
                data-testid="duplicates-btn"
                title="Find and merge duplicate client records"
                className={cn(
                  dupCount > 0 && "border-amber-400 bg-amber-50 hover:bg-amber-100",
                )}
              >
                <GitMerge className="h-4 w-4 mr-1.5" />
                Duplicates
                {dupCount > 0 && (
                  <Badge className="ml-1.5 bg-amber-100 text-amber-800 border-amber-300 text-2xs">
                    {dupCount}
                  </Badge>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={syncAcuity}
              disabled={syncing}
              data-testid="sync-acuity-clients-btn"
              title="Pull the latest Acuity bookings into the Clients directory"
            >
              {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Sync Acuity
            </Button>
            <Button
              variant="outline"
              onClick={() => setNewOpen(true)}
              data-testid="new-client-btn"
              title="Create a new client record — email is optional"
            >
              <UserPlus2 className="h-4 w-4 mr-1.5" /> New client
            </Button>
            <Button
              onClick={() => setAdhocOpen(true)}
              data-testid="open-client-by-email-btn"
            >
              <UserPlus className="h-4 w-4 mr-1.5" /> Open client by email
            </Button>
          </div>
        )}
      </div>

      <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="clients-directory-card">
        {/* Prominent search — moved to the top of the card so it's the
            first thing operators reach. Big input, magnifying glass on
            the left, one-click clear on the right, and a hint under it
            listing what the query matches so users don't have to guess. */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email or phone number…"
              className="h-12 pl-10 pr-10 text-base bg-white border-zinc-300 focus-visible:ring-indigo-600"
              data-testid="clients-search"
              autoFocus
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900"
                data-testid="clients-search-clear"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="text-2xs text-zinc-500">
            Matches on client name, email address, phone number and additional emails.
          </div>
        </div>

        {/* Filter chips */}
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="client-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                filter === f.key
                  ? "bg-zinc-950 text-white border-zinc-950"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50",
              )}
              data-testid={`client-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            {rowCountLabel}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Refresh"}
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-directory-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-4">Client</th>
                <th className="py-2 pr-4 text-right">Subs</th>
                <th className="py-2 pr-4 text-right">Open</th>
                <th className="py-2 pr-4">Latest PDQ-SI</th>
                <th className="py-2 pr-4">Latest ONERO</th>
                <th className="py-2 pr-4">Last activity</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-zinc-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-zinc-500">
                  No matches for the current filter.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.email_key}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                  onClick={() => openClient(r)}
                  data-testid={`clients-row-${r.email_key}`}
                >
                  <td className="py-3 pr-4">
                    <div className="font-medium text-zinc-950 flex items-center gap-2">
                      {r.has_health_risk && (
                        <span
                          className="relative inline-flex h-2 w-2 shrink-0"
                          data-testid={`client-risk-dot-${r.email_key}`}
                          title={
                            r.risk_labels?.length
                              ? `Health risk on file: ${r.risk_labels.join(" · ")}`
                              : "Unacknowledged critical alert or severe allergy on file"
                          }
                        >
                          <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-70 animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600 ring-2 ring-white" />
                        </span>
                      )}
                      {r.client_name || "—"}
                      {r.has_intake === false && (
                        <span
                          className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                          title="Client seen in Acuity but has never filled an intake form"
                        >
                          no intake yet
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono-num">{r.client_email}</div>
                  </td>
                  <td className="py-3 pr-4 text-right font-mono-num text-zinc-700">
                    {r.total_submissions}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    {r.open_count > 0 ? (
                      <Badge
                        className="bg-amber-100 text-amber-800 border-amber-200 border text-2xs uppercase tracking-wider"
                        title={`${r.open_count} onboarding form${r.open_count > 1 ? "s" : ""} awaiting submission`}
                        data-testid={`clients-row-pending-${(r.client_email || "").toLowerCase()}`}
                      >
                        {r.open_count} pending
                      </Badge>
                    ) : (
                      <span className="text-xs text-zinc-400">0</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    {r.latest_pdq39_summary != null ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 px-2 py-0.5 text-xs font-mono-num">
                        {r.latest_pdq39_summary.toFixed(1)} / 100
                      </span>
                    ) : (<span className="text-xs text-zinc-400">—</span>)}
                  </td>
                  <td className="py-3 pr-4">
                    {r.latest_onero_composite != null ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 border border-sky-200 text-sky-900 px-2 py-0.5 text-xs font-mono-num">
                        {r.latest_onero_composite.toFixed(1)} / 100
                      </span>
                    ) : (<span className="text-xs text-zinc-400">—</span>)}
                  </td>
                  <td className="py-3 pr-4 text-xs text-zinc-600">
                    {fmtDT(r.last_activity_at)}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => openClient(r)}
                        data-testid={`client-open-${r.email_key}`}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                      {editable && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setSmsFor(r)}
                          disabled={!r.client_phone}
                          title={r.client_phone ? "Send SMS" : "No phone on file"}
                          data-testid={`client-sms-${r.email_key}`}
                        >
                          <MessageSquare className="h-3.5 w-3.5 mr-1" /> SMS
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <QuickSmsDialog
        open={!!smsFor}
        onClose={() => setSmsFor(null)}
        clientEmail={smsFor?.client_email}
        clientName={smsFor?.client_name}
        clientPhone={smsFor?.client_phone}
      />
      <DuplicateClientsDialog
        open={dupOpen}
        onOpenChange={setDupOpen}
        onMerged={() => { load(); loadDupCount(); }}
      />
      <OpenClientByEmailDialog
        open={adhocOpen}
        onClose={() => setAdhocOpen(false)}
        onOpen={(email) => {
          setAdhocOpen(false);
          openClient(email);
        }}
      />
      <NewClientDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={async ({ client_id, client_email, was_existing, also_matched }) => {
          setNewOpen(false);
          await load();
          if (was_existing) {
            toast.info("A client with that name + email already exists — opening theirs.");
          } else if (also_matched && also_matched.length > 0) {
            toast.success(
              `Created — this email is also shared with ${also_matched.map((m) => m.full_name).join(", ")}. Both records are searchable independently.`,
              { duration: 8000 },
            );
          }
          // Prefer canonical uuid route when we have it — the workspace
          // URL folds it in via ``?as=<cid>``.
          if (client_id) {
            navigate(workspacePathFor(client_email, client_id));
          } else {
            navigate(`/clients/${encodeURIComponent(client_email)}`);
          }
        }}
      />
    </div>
  );
}


/**
 * NewClientDialog — minimal manual-create form. Only name is required.
 * When email is blank, backend synthesises a deterministic
 * `firstname.lastname.dob@no-email.local` key so the client is still
 * fully linkable across the app.
 */
function NewClientDialog({ open, onClose, onCreated }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setDob("");
    }
  }, [open]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!firstName.trim() && !lastName.trim()) {
      toast.error("First or last name is required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/intake/clients/manual", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        dob: dob.trim() || null,
      });
      toast.success(
        r.data.was_existing ? "Client already existed — opening theirs" : "Client created",
        r.data.generated_email
          ? { description: `No email supplied — created synthetic key ${r.data.client_email}` }
          : undefined,
      );
      onCreated?.(r.data);
    } catch (err) {
      toast.error("Create failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
          <DialogDescription>
            Only the name is required. Email is optional — leave blank
            for clients who don&apos;t have one and we&apos;ll generate a
            stable placeholder so their record still links to SOAP
            notes, programs and documents.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="new-first">First name</Label>
              <Input id="new-first" value={firstName}
                     onChange={(e) => setFirstName(e.target.value)}
                     autoFocus data-testid="new-client-first" />
            </div>
            <div>
              <Label htmlFor="new-last">Last name</Label>
              <Input id="new-last" value={lastName}
                     onChange={(e) => setLastName(e.target.value)}
                     data-testid="new-client-last" />
            </div>
          </div>
          <div>
            <Label htmlFor="new-email">Email <span className="text-zinc-400 font-normal">(optional)</span></Label>
            <Input id="new-email" type="email" value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   placeholder="jane@example.com"
                   data-testid="new-client-email" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="new-phone">Phone <span className="text-zinc-400 font-normal">(optional)</span></Label>
              <Input id="new-phone" value={phone}
                     onChange={(e) => setPhone(e.target.value)}
                     placeholder="+61…"
                     data-testid="new-client-phone" />
            </div>
            <div>
              <Label htmlFor="new-dob">DOB <span className="text-zinc-400 font-normal">(optional)</span></Label>
              <Input id="new-dob" value={dob}
                     onChange={(e) => setDob(e.target.value)}
                     placeholder="YYYY-MM-DD"
                     data-testid="new-client-dob" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="new-client-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating…</> : "Create client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/**
 * (Removed 2026-02-15) RecentWalkinsStrip — replaced by the
 * "Active today" filter on the directory. The filter uses the same
 * Acuity today-appointments feed and surfaces those clients as full
 * directory rows (with intake counts, phone, PDQ trend), which is
 * strictly more informative than the compact strip was.
 */


/**
 * OpenClientByEmailDialog — jump to the ClientDetail page for any Acuity
 * client, even one who has never filled an intake form. The detail page
 * gracefully handles missing submissions and pulls appointments directly
 * from Acuity, so SOAP recording, SMS and appointment view all work.
 *
 * Search modes
 *   - By email — as-you-type substring across Acuity appointments
 *   - By name  — first-name OR last-name lookup against Acuity so
 *     front-desk can find a walk-in without knowing their email
 */
function OpenClientByEmailDialog({ open, onClose, onOpen }) {
  const [mode, setMode] = useState("email");  // "email" | "name"
  const [term, setTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) { setTerm(""); setSuggestions([]); return; }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = term.trim();
    if (mode === "email" && (t.length < 3 || !t.includes("@"))) {
      setSuggestions([]); return;
    }
    if (mode === "name" && t.length < 2) {
      setSuggestions([]); return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const today = new Date();
        const past = new Date(today.getTime() - 365 * 86400_000);
        const future = new Date(today.getTime() + 60 * 86400_000);
        const iso = (d) => melDate(d);
        const baseParams = {
          minDate: iso(past), maxDate: iso(future), max: 50,
          // Client search only needs name / email / phone. Skipping the
          // Acuity price enrichment drops this from ~30 s to ~1 s on
          // busy accounts and keeps typing-latency snappy.
          enrich_price: false,
          enrich_intake: false,
        };
        const params = mode === "email"
          ? { ...baseParams, email: t }
          // Acuity supports firstName / lastName substring — try both against
          // the same term so operators can type either without switching.
          : { ...baseParams, firstName: t };
        let r = await api.get("/appointments", { params });
        let list = r.data || [];
        // Second pass for name mode — also try as last-name
        if (mode === "name") {
          const r2 = await api.get("/appointments", {
            params: { ...baseParams, lastName: t },
          });
          list = list.concat(r2.data || []);
        }
        if (cancelled) return;
        const seen = new Map();
        for (const a of list) {
          const key = (a.email || "").toLowerCase();
          if (!key || seen.has(key)) continue;
          seen.set(key, {
            email: a.email,
            name: [a.firstName, a.lastName].filter(Boolean).join(" "),
            phone: a.phone,
          });
        }
        setSuggestions(Array.from(seen.values()).slice(0, 10));
      } catch {
        setSuggestions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [term, open, mode]);

  function submit() {
    const clean = term.trim().toLowerCase();
    if (mode === "email") {
      if (!clean.includes("@")) return;
      onOpen(clean);
    } else if (suggestions[0]) {
      onOpen(suggestions[0].email.toLowerCase());
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="open-client-by-email-dialog">
        <DialogHeader>
          <DialogTitle>Open client</DialogTitle>
          <DialogDescription>
            Jump to any client&apos;s profile — even one who hasn&apos;t filled
            an intake form yet. Their Acuity appointments and any SOAP
            notes you record will appear on the profile.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 mb-2">
          <button
            onClick={() => { setMode("email"); setSuggestions([]); }}
            className={cn(
              "px-3 py-1 text-xs rounded-full border transition-colors",
              mode === "email" ? "bg-zinc-950 text-white border-zinc-950"
                               : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50",
            )}
            data-testid="adhoc-mode-email"
          >
            By email
          </button>
          <button
            onClick={() => { setMode("name"); setSuggestions([]); }}
            className={cn(
              "px-3 py-1 text-xs rounded-full border transition-colors",
              mode === "name" ? "bg-zinc-950 text-white border-zinc-950"
                              : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50",
            )}
            data-testid="adhoc-mode-name"
          >
            By name
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">
              {mode === "email" ? "Client email" : "First or last name"}
            </Label>
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              placeholder={mode === "email" ? "client@example.com" : "e.g. Sarah or Smith"}
              className={cn("mt-1", mode === "email" && "font-mono-num")}
              autoFocus
              data-testid="open-client-email-input"
            />
          </div>
          {searching && (
            <div className="text-xs text-zinc-500 flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking Acuity for matches…
            </div>
          )}
          {suggestions.length > 0 && (
            <ul className="border border-zinc-200 rounded-md divide-y" data-testid="open-client-suggestions">
              {suggestions.map((s) => (
                <li
                  key={s.email}
                  className="px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer flex items-center justify-between gap-2"
                  onClick={() => onOpen(s.email.toLowerCase())}
                  data-testid={`open-client-suggestion-${(s.email || "").toLowerCase()}`}
                >
                  <div>
                    <div className="font-medium text-zinc-900">{s.name || s.email}</div>
                    <div className="text-2xs text-zinc-500 font-mono-num">{s.email}</div>
                  </div>
                  <span className="text-2xs text-zinc-500">from Acuity</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={mode === "email" ? !term.includes("@") : suggestions.length === 0}
            data-testid="open-client-submit"
          >
            Open profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
